/* eslint-disable @angular-eslint/no-empty-lifecycle-method */
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  input,
  inject,
  computed,
  linkedSignal,
  effect,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule } from '@angular/forms';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
  MatSlideToggleChange,
  MatSlideToggleModule
} from '@angular/material/slide-toggle';
import { MatSliderDragEvent, MatSliderModule } from '@angular/material/slider';
import { Feature } from 'ol';
import { Layer } from 'ol/layer';
import LayerGroup from 'ol/layer/Group';
import VectorLayer from 'ol/layer/Vector';
import { switchMap, of, map, distinctUntilChanged } from 'rxjs';
import { PaddockDexie } from 'src/app/models/paddock.model';
import { ClippedLayerService } from 'src/app/services/BaseLayerServices/clippedLayer.service';
import { PropertyService } from 'src/app/services/property.service';
import { versatilityGradientColors } from 'src/app/_helpers/layer.styles';
import { ChildEntity } from 'typeorm';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SpaceBeforeCapPipe } from 'src/app/pipes/space_before_capitial.pipe';
import { filter } from 'rxjs';
import { layerOpacity$, layerVisibility$ } from 'src/app/_helpers/rxjs_ol';
import {
  NgpSelect,
  NgpSelectDropdown,
  NgpSelectOption,
  NgpSelectPortal
} from 'ng-primitives/select';
import { AccordionComponent, AccordionItemComponent, SwitchComponent, SliderComponent, SelectComponent, SelectModel } from '@aetherion/ui';

@Component({
  selector: 'app-analysis-group',
  templateUrl: './analysis-group.component.html',
  styleUrls: ['./analysis-group.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    SpaceBeforeCapPipe,
    AccordionComponent,
    AccordionItemComponent,
    SwitchComponent,
    SliderComponent,
    SelectComponent,
    FormsModule,
    NgpSelectOption
  ]
})
export class AnalysisGroupComponent implements OnInit {
  private clippedLayerService = inject(ClippedLayerService);
  private propertyService = inject(PropertyService);

  readonly layerGroup = input<LayerGroup>(undefined);

  readonly paddockSelect = input<boolean>(false);

  readonly paddocks = input<PaddockDexie[]>([]);

  @Output() showPaddocks = new EventEmitter<PaddockDexie[]>();
  @Output() downloadLayers = new EventEmitter<boolean>();

  activeLayer: VectorLayer<any> | Layer<any, any> = null;

  paddockSelectModel = computed<SelectModel[]>(() => {
    const paddocks = this.paddocks();

    if(paddocks == null)
      return [];

    return paddocks.map<SelectModel>((paddock) => ({id: paddock.paddockID, displayName: paddock.name}))
  });


  selectedPaddockIDs = signal([]);

  selectedPaddocks = computed(() => {
    const selectedPaddockIDs = this.selectedPaddockIDs();
    const paddocks = this.paddocks();


    return paddocks.filter(e => selectedPaddockIDs.includes(e.paddockID));
  })
  colorIndex = [];

  activeProperty = toSignal(
    this.propertyService.activeProperty.pipe(
      map((val) => (val == null ? false : true))
    ),
    { initialValue: false }
  );

  isDownloaded = toSignal(
    this.propertyService.activeProperty.pipe(
      switchMap((property) =>
        property
          ? this.clippedLayerService.getClippedLayers_Property(
              property.propertyID
            )
          : of([])
      ),
      map((layers) =>
        layers.some((l) => l.layerName.toLowerCase() === 'topex')
      ),
      distinctUntilChanged()
    ),
    { initialValue: false }
  );

  baseLayer$ = toObservable(this.layerGroup).pipe(
    filter((g): g is LayerGroup => g !== null)
  );

  opacity$ = this.baseLayer$.pipe(switchMap((g) => layerOpacity$(g)));

  visibility$ = this.baseLayer$.pipe(switchMap((g) => layerVisibility$(g)));

  // OL has opacity as a scale from 0 - 1, I.E 0.1 0.2 etc, we are mapping it to be easier to display percentages
  opacity = toSignal(this.opacity$.pipe(map((opacity) => opacity * 100)), {
    initialValue: 100
  });

  visible = toSignal(this.visibility$, { initialValue: true });

  opacityLabel = computed(() => {
    const opacity = this.opacity() as Number;

    return `${opacity.toFixed(2)}%`;
  });

  title = computed(() =>
    this.layerGroup() == null ? 'Unknown' : this.layerGroup().get('title')
  );

  layers = computed(() => {
    return this.layerGroup() == null ? [] : this.layerGroup().getLayersArray();
  });

  layerMap = computed(() => {
   const layers = this.layers();

     return layers.reduce<Record<string, Layer>>((acc, layer) => {
    acc[layer.get('label')] = layer;
    return acc;
  }, {});
  })

  layerMetaData = computed<SelectModel[]>(() => {
    const layers = this.layers();

    if(layers == null)
      return [];

    return layers.map<SelectModel>((lyr) => ({
      displayName: lyr.get('label'),
      id: lyr.get('title'),
    }));
  });

  selectedCrop = linkedSignal(() => {
    const metaData = this.layerMetaData();

    const initialLayer = metaData.find((data) => {
      return data.displayName == 'Radiata Pine';
    });

    return initialLayer.id;
  });

  constructor() {
    effect(() => {
      const selectedCrop = this.selectedCrop();
      const layerMetaData = this.layerMetaData();
      const layerMap = this.layerMap();

      if (layerMetaData.length === 0) {
        return;
      }

      layerMetaData.forEach((meta) => {
        const shouldShow = selectedCrop && meta.id === selectedCrop;
        const layer = layerMap[meta.displayName].setVisible(shouldShow)
      });
    });

    effect(() => {
      const selectedPaddocks = this.selectedPaddocks();

      if(selectedPaddocks == null)
      {
        return;
      }

      this.showPaddocks.emit(selectedPaddocks)

    })

    effect(() => {
      console.log(this.layerMetaData())
    })
  }

  ngOnInit(): void {
    for (const [key, value] of Object.entries(versatilityGradientColors)) {
      let colorIndex = {
        name: key,
        color: `rgba(${value})`
      };

      this.colorIndex.push(colorIndex);
    }

  }



  setLayerGroupOpacity(opacity): void {
    this.layerGroup().setOpacity(opacity / 100);
  }



  setLayerGroupVisibility(event): void {
    this.layerGroup().setVisible(event);
  }


}
